import { useEffect, useMemo } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Badge, Empty, Space, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { ListOvrProjectsParams } from '../../../../../hooks/online-virtual-research';
import { useAuth } from '../../../../../hooks/useAuth';
import { RootState, useAppDispatch } from '../../../../../store';
import {
  OvrProjectsState,
  fetchOvrProjects,
} from '../../../../../store/features/ovrProjects/ovrProjectsSlice';
import { UserPermissions } from '../../../../../util';
import { DrawerHashRoute } from '../../../../containers/Drawers/types';
import Button from '../../../../elements/Button';
import { RoutePath } from '../../../AppRoot/types';
import OVRGridList from '../../../OnlineVirtualResearchView/components/OVRProjectItemsList/components/OVRGridList';

const { Title } = Typography;

const RecentOVRProjectsList = () => {
  const listOVRProjectsParams = useMemo(
    (): ListOvrProjectsParams => ({
      _offset: 0,
      _limit: 5,
      _order_by: 'updated_at:desc',
      _columns: '*,client.*,createdByUser.*',
      status: 'active',
    }),
    []
  );

  const dispatch = useAppDispatch();
  const {
    value: projects,
    fetchingOvrProjects,
    totalCount,
  } = useSelector<RootState, OvrProjectsState>((state) => state.ovrProjects);
  const hasOVRProjects = !(!fetchingOvrProjects && projects.length === 0);

  const { hasPermission } = useAuth();

  useEffect(() => {
    dispatch(fetchOvrProjects({ params: listOVRProjectsParams }));
  }, [dispatch, listOVRProjectsParams]);

  return (
    <section>
      <header style={{ marginBottom: '1rem' }}>
        <Space direction="horizontal">
          <Title level={4}>Recent OVR Projects</Title>
          <Badge
            size="default"
            count={totalCount}
            className="total-count-badge"
            overflowCount={999}
          />
        </Space>
      </header>
      {hasOVRProjects ? (
        <>
          <OVRGridList
            skeletonItemLength={5}
            loading={fetchingOvrProjects}
            projects={projects}
            hasMore={false}
          />
          <footer style={{ marginBottom: '1rem' }}>
            <Link to={RoutePath.OVR} style={{ color: '#8e24aa' }}>
              View All Projects
            </Link>
          </footer>
        </>
      ) : (
        <Empty
          description="You currently have no online virtual research projects."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          {hasPermission(UserPermissions.OvrProjectsCreate) ? (
            <Link to={DrawerHashRoute.OVRForm}>
              <Button type="primary" data-cy="create-new-ovr-project-btn">
                Create New Project
                <PlusOutlined />
              </Button>
            </Link>
          ) : null}
        </Empty>
      )}
    </section>
  );
};

export default RecentOVRProjectsList;
